<template>
  <div class="min-h-screen bg-gray-50">
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-12 w-12 border-4 border-[#E65100] border-t-transparent mb-4"
        ></div>
        <p class="text-gray-600">{{ language === 'el' ? 'Φόρτωση πληροφοριών προορισμού...' : 'Loading destination information...' }}</p>
      </div>
    </div>

    <div v-else-if="error" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="text-red-500 text-xl mb-4">{{ error }}</div>
        <router-link :to="{ name: getRouteName('home') }" class="text-[#E65100] hover:underline">
          {{ language === 'el' ? 'Επιστροφή στην Αρχική Σελίδα' : 'Return to Homepage' }}
        </router-link>
      </div>
    </div>

    <template v-else-if="destination">
      <!-- Hero Section -->
      <section
        class="relative min-h-[500px] flex items-center justify-center overflow-hidden bg-gradient-to-r from-[#1a2a4d] to-[#233862]"
        :style="{ backgroundImage: `url(${destination.image})` }"
      >
        <div class="absolute inset-0 bg-gradient-to-b from-[#233862]/90 to-[#1a2a4d]/90"></div>
        <div class="relative z-10 container mx-auto px-4 py-12">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div class="text-left">
              <h1 class="text-4xl md:text-5xl font-bold text-white mb-4">
                {{ language === 'el' ? 'Ακτοπλοϊκά Εισιτήρια για ' : 'Ferry Tickets to ' }}{{ destination.name }}
              </h1>
              <p class="text-xl text-white/80">
                {{ destination.content.intro }}
              </p>
            </div>
            <div>
              <iframe
                width="100%"
                height="450"
                scrolling="no"
                frameborder="0"
                :src="`https://www.ferryhopper.com/en/embed/simple?aff_uid=webee&color=E65100&arrival=${destination.id}`"
                class="mx-auto"
                :title="'Book ferry tickets to ' + destination.name"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <!-- Routes Info -->
      <section class="py-12 bg-gray-50">
        <div class="container mx-auto px-4">
          <h2 class="text-3xl font-bold text-[#233862] mb-8 text-center">
            {{ language === 'el' ? 'Διαθέσιμα Δρομολόγια για ' : 'Available Routes to ' }}{{ destination.name }}
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div
              v-for="route in destination.routes"
              :key="route.from"
              class="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
            >
              <div class="flex items-center justify-between mb-4">
                <div>
                  <div class="text-sm text-[#E65100] font-medium">{{ language === 'el' ? 'Από' : 'From' }}</div>
                  <div class="text-xl font-semibold text-[#233862]">{{ route.from }}</div>
                </div>
                <div class="text-[#E65100]">
                  <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </div>
                <div>
                  <div class="text-sm text-[#E65100] font-medium">{{ language === 'el' ? 'Προς' : 'To' }}</div>
                  <div class="text-xl font-semibold text-[#233862]">{{ destination.name }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Destination Info -->
      <section class="py-12 bg-white">
        <div class="container mx-auto px-4">
          <div class="max-w-4xl mx-auto">
            <h2 class="text-3xl font-bold text-[#233862] mb-8">{{ language === 'el' ? 'Σχετικά με ' : 'About ' }}{{ destination.name }}</h2>
            <div class="prose prose-lg max-w-none">
              <p class="text-gray-600 mb-6">{{ destination.content.intro }}</p>

              <h3 class="text-xl font-semibold text-[#233862] mb-4">{{ language === 'el' ? 'Σημεία Ενδιαφέροντος' : 'Highlights' }}</h3>
              <ul class="space-y-2 mb-6">
                <li
                  v-for="highlight in destination.content.highlights"
                  :key="highlight"
                  class="flex items-center text-gray-600"
                >
                  <svg
                    class="w-5 h-5 mr-2 text-[#E65100]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {{ highlight }}
                </li>
              </ul>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-xl font-semibold text-[#233862] mb-4">{{ language === 'el' ? 'Πληροφορίες Λιμανιού' : 'Port Information' }}</h3>
                  <p class="text-gray-600">{{ destination.content.portInfo }}</p>
                </div>
                <div class="bg-gray-50 rounded-lg p-6">
                  <h3 class="text-xl font-semibold text-[#233862] mb-4">{{ language === 'el' ? 'Συμβουλές Μεταφοράς' : 'Transport Tips' }}</h3>
                  <p class="text-gray-600">{{ destination.content.transportTips }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import destinationsEn from '@/data/destinations.json'
import destinationsEl from '@/data/destinations.el.json'

const route = useRoute()
const destination = ref(null)
const loading = ref(true)
const error = ref(null)

// Get language from route - 'el' for Greek paths, 'en' for all others
const language = computed(() => {
  return route.path.includes('/el/') ? 'el' : 'en'
})

// Compute route names with language prefix
const getRouteName = (baseName) => {
  return language.value === 'el' ? `el${baseName}` : baseName
}

// Import all images
const images = import.meta.glob('@/assets/images/**/*.{jpg,jpeg,png,gif,webp}', { eager: true })

// Function to get image URL
const getImageUrl = (imagePath) => {
  try {
    // Remove the @ symbol from the path
    const cleanPath = imagePath.replace('@', '')
    const key = Object.keys(images).find(key => key.includes(cleanPath.split('/').pop()))
    return key ? images[key].default : ''
  } catch (error) {
    console.error('Error loading image:', error)
    return ''
  }
}

onMounted(async () => {
  try {
    // Select destinations based on language
    const destinationsData = language.value === 'el' ? destinationsEl : destinationsEn
    const foundDestination = destinationsData.destinations.find((d) => d.id === route.params.id)
    
    if (!foundDestination) {
      error.value = language.value === 'el' ? 'Ο προορισμός δεν βρέθηκε' : 'Destination not found'
    } else {
      destination.value = {
        ...foundDestination,
        image: getImageUrl(foundDestination.image)
      }
      
      // Update meta tags for SEO
      const title = language.value === 'el' 
        ? `${destination.value.name} - Ακτοπλοϊκά Εισιτήρια & Πληροφορίες | Aktoploika`
        : `${destination.value.name} - Ferry Tickets & Information | Aktoploika`
      
      document.title = title

      // Update meta description
      const description = language.value === 'el'
        ? `Κλείστε ακτοπλοϊκά εισιτήρια για ${destination.value.name}. Βρείτε δρομολόγια, τιμές και χρήσιμες πληροφορίες για το ταξίδι σας.`
        : `Book ferry tickets to ${destination.value.name}. Find schedules, prices and useful information for your trip.`

      const metaDescription = document.querySelector('meta[name="description"]')
      if (metaDescription) {
        metaDescription.setAttribute('content', description)
      } else {
        const meta = document.createElement('meta')
        meta.setAttribute('name', 'description')
        meta.setAttribute('content', description)
        document.head.appendChild(meta)
      }

      // Update Open Graph tags
      const ogTags = {
        'og:title': title,
        'og:description': description,
        'og:type': 'website',
        'og:image': destination.value.image,
        'og:url': window.location.href
      }

      Object.entries(ogTags).forEach(([property, content]) => {
        let meta = document.querySelector(`meta[property="${property}"]`)
        if (!meta) {
          meta = document.createElement('meta')
          meta.setAttribute('property', property)
          document.head.appendChild(meta)
        }
        meta.setAttribute('content', content)
      })

      // Add language meta tag
      let langMeta = document.querySelector('meta[property="og:locale"]')
      if (!langMeta) {
        langMeta = document.createElement('meta')
        langMeta.setAttribute('property', 'og:locale')
        document.head.appendChild(langMeta)
      }
      langMeta.setAttribute('content', language.value === 'el' ? 'el_GR' : 'en_US')
    }
  } catch (err) {
    error.value = language.value === 'el' 
      ? 'Παρουσιάστηκε σφάλμα κατά τη φόρτωση του προορισμού' 
      : 'An error occurred while loading the destination'
    console.error('Error:', err)
  } finally {
    loading.value = false
  }
})
</script>
